<template>
  <div class="modal-overflow" v-if="dataLoaded">
    <div v-if="!purchaseOrder">
      <h5><span style="color:red">Error:</span> Could not find this Purchase Order. This should never happen</h5>
    </div>
    <div v-else-if="!originalPurchaseOrder">
      <h5><span style="color:red">Error:</span> Could not find an Original purchase order with this builderOrderNumber to modify.</h5>
    </div>
    <div v-else-if="!originalPurchaseOrder.matchingProjectId">
      <h5><span style="color:red">Error:</span> The Original Purchase Order for Builder PO#:
        <router-link :to="`/admin/supply-pro/${originalPurchaseOrder.id}`"
                      @click.native="$emit('close')">
          {{ purchaseOrder.header.builderOrderNumber }}
        </router-link>
        was found, but it has not yet been accepted/matched to a PTRAC project. Please process this PO first, before processing changes.
      </h5>
      <div style="display:flex; justify-content:space-around;">
        <router-link :to="`/admin/supply-pro/${originalPurchaseOrder.id}`"
                      @click.native="$emit('close')">
          <button class="btn btn-md btn-fill btn-info">Go There Now</button>
        </router-link>
      </div>
    </div>
    <div v-else-if="ptracProject && ptracJobs.length === 0">
      <h5><span style="color:red">Error:</span> The Original PO with Builder PO#:
        <router-link :to="`/admin/supply-pro/${originalPurchaseOrder.id}`"
                      @click.native="$emit('close')">
          {{ purchaseOrder.header.builderOrderNumber }}
        </router-link>
        has already been matched to a PTRAC project, but no jobs have been associated with it yet.</h5>
      <div style="display:flex; justify-content:space-around;">
        <router-link :to="`/admin/supply-pro/${originalPurchaseOrder.id}`"
                      @click.native="$emit('close')">
          <button class="btn btn-md btn-fill btn-info">Go There Now</button>
        </router-link>
      </div>
    </div>
    <div v-else-if="firstNotAcceptedVersionOfChangePO">
      <h5><span style="color:red">Error:</span> A Change PO that was was received before this one has not been processed yet.</h5>
      <div style="display:flex; justify-content:space-around;">
        <button
                @click="handleLinkToPreviousPO(firstNotAcceptedVersionOfChangePO.id, firstNotAcceptedVersionOfChangePO.purchaseOrderType)"
                class="btn btn-md btn-fill btn-info">
          Go There Now
        </button>
      </div>
    </div>
    <div v-else>
      <h5>
        <strong>{{ purchaseOrder.purchaseOrderType }} - </strong>
        <span>Purchase Order #{{ purchaseOrder.header.builderOrderNumber }}</span>
        <span style="font-style:italic; font-size:medium;"> from {{ purchaseOrder.header.builder.name }}</span>
      </h5>
      <project-details :ptracProject="ptracProject"></project-details>
      <br>
      <!-- transition needs work . . . -->
      <transition mode="out-in">
        <div v-if="index === jobIndexToEdit" v-for="(job, index) in ptracJobs" :key="job.id" ref="jobComponent">
          <div class="card">
            <!-- same handler called with different emitted data from different dynamic components  requestedChangesLoaded is only emitted from EditJobChange-->
            <component :is="purchaseOrder.purchaseOrderType"
                      @requestedChangesLoaded="populateRequestedChangesMade()"
                      @requestedJobChanged="handleRequestedChangeMade(index, $event)"
                      @requestedChangeMade="handleRequestedChangeMade($event, true)"
                      :jobId="job.id"
                      :purchaseOrder="purchaseOrder">
            </component>
          </div>
        </div>
      </transition>
      <div class="row" v-if="purchaseOrder.purchaseOrderType === 'Cancellation' || purchaseOrder.purchaseOrderType === 'Reschedule'">
        <div class="col-md-2"></div>
        <div class="col-md-8">
          <div class="button-group" v-if="ptracJobs.length > 1">
            <button class="btn btn-md btn-fill btn-info" :disabled="jobIndexToEdit + 1 <= 1" @click="jobIndexToEdit -= 1">Edit Previous Job</button>
            <button class="btn btn-md btn-fill btn-default" disabled>Job {{jobIndexToEdit + 1}} of {{ ptracJobs.length }}</button>
            <button class="btn btn-md btn-fill btn-info" :disabled="jobIndexToEdit + 1 >= ptracJobs.length" @click="jobIndexToEdit += 1">Edit Next Job</button>
          </div>
        </div>
        <div class="col-md-2"></div>
      </div>
      <button type="button" :disabled="!allRequestedChangesMade" class="btn btn-md btn-fill btn-info pull-right" @click="confirmChanges">Confirm Changes</button>
    </div>
  </div>
</template>

<script>
import {mapGetters} from 'vuex'
import Constants from 'src/utilities/constants'
import {notifyError, notifySuccess} from 'src/utilities/utils'
import ProjectDetails from './ProjectDetails'
import {OK} from 'http-status-codes'

const Cancellation = () => import('./EditJobCancellation')
const Reschedule = () => import('./EditJobReschedule')
const Change = () => import('./EditJobChange')

export default {
  name: "change-purchase-order",
  components: {
      Cancellation,
      Reschedule,
      Change,
      ProjectDetails
    },
  computed: {
    ...mapGetters({
      changesFound: 'supplyPro/purchaseOrder/changesFound',
      ptracJobs: 'supplyPro/purchaseOrder/ptracJobs',
      ptracProject: 'supplyPro/purchaseOrder/ptracProject',
      purchaseOrder: 'supplyPro/purchaseOrder/purchaseOrder',
      originalPurchaseOrder: 'supplyPro/purchaseOrder/originalPurchaseOrder',
      firstNotAcceptedVersionOfChangePO: 'supplyPro/purchaseOrder/firstNotAcceptedVersionOfChangePO',
      mostRecentAcceptedVersionOfChangePO: 'supplyPro/purchaseOrder/mostRecentAcceptedVersionOfChangePO',
    }),

    allRequestedChangesMade() {
      for (let i = 0; i < this.requestedChangesMade.length; i++) {
        if (this.requestedChangesMade[i] === false) {
          return false
        }
      }
      return true
    }
  },
  data() {
    return {
      dataLoaded: false,
      constants: Constants,
      jobIndexToEdit: 0,
      requestedChangesMade: []
    }
  },
  props: ['data'],
  methods: {
    async loadData(){
      const response = await this.$store.dispatch('supplyPro/purchaseOrder/fetchDataForChangeOrCancel', this.data.purchaseOrderId)
      if (OK === response.status) {
        this.dataLoaded = true
      } else {
        notifyError(this, "Error loading Change PO data. " + response.data.toString())
      }
    },
    async confirmChanges() {
        if (this.allRequestedChangesMade) {
          const response = await this.$store.dispatch('supplyPro/purchaseOrder/markPurchaseOrderAsAccepted')

          if (OK !== response.status) {
            notifyError(this, `Error saving changes. ${response.data.toString()}`)
            return
          }

          notifySuccess(this, "Changes Confirmed! Sending response to SPConnect")
          this.$emit('close')
        } else {
          notifyError(this, "Cant confirm changes until all jobs are changed and saved")
        }
      },
      handleRequestedChangeMade(index, value) {
        this.$set(this.requestedChangesMade, index, value)
      },
      handleLinkToPreviousPO(purchaseOrderId, purchaseOrderType) {
        // The firstNotAcceptedVersionOfChangePO shouldn't ever be an original PO (would get caught by earlier condition), but just in case . . .
        if (purchaseOrderType === this.constants.SUPPLYPRO.PO_TYPES.ORIGINAL) {
          this.$router.push({path: `/admin/supply-pro/${purchaseOrderId}`})
        } else {
          // refresh modal with data from firstNotAcceptedVersionOfChangePO
          this.dataLoaded = false
          this.data.purchaseOrderId = purchaseOrderId
          this.loadData()
        }
      },
      populateRequestedChangesMade() {
        if (this.purchaseOrder.purchaseOrderType === this.constants.SUPPLYPRO.PO_TYPES.CANCELLATION
              || this.purchaseOrder.purchaseOrderType === this.constants.SUPPLYPRO.PO_TYPES.RESCHEDULE) {
          for (let i = 0; i < this.ptracJobs.length; i++) {
            this.requestedChangesMade.push(false)
          }
        } else if (this.purchaseOrder.purchaseOrderType === this.constants.SUPPLYPRO.PO_TYPES.CHANGE) {
          for (let i = 0; i < this.changesFound.length; i++) {
            this.requestedChangesMade.push(false)
          }
        }
      }
  },
  async created() {
    await this.loadData()
    this.populateRequestedChangesMade()
  },
  beforeDestroy() {
    this.$store.commit('supplyPro/purchaseOrder/reset')
  }
}
</script>
<style scoped>

.v-enter-active,
.v-leave-active {
  transition: opacity 0.5s ease;
}

.v-enter-from,
.v-leave-to {
  opacity: 0;
}
</style>
